// ======================================
// NammaVeetuInteriors - Main Entry Point
// ======================================

// Import styles
import './styles/global.css';
import './styles/components/header.css';
import './styles/components/footer.css';
import './styles/components/cards.css';
import './styles/pages/home.css';
import './styles/pages/services.css';
import './styles/pages/packages.css';
import './styles/pages/projects.css';
import './styles/pages/phase2.css';
import './styles/pages/auth.css';
import './styles/pages/admin.css';

// Import components
import { renderHeader, renderFooter, renderStickyCTA, renderBottomNav, renderLeadModal, initUI } from './components.js';
import { router } from './router.js';

// Import Phase 1 pages
import { renderHomePage, initHomePage } from './pages/home.js';
import { renderServicesPage, renderServiceDetailPage, initServicesPage, initServiceDetailPage } from './pages/services.js';
import { renderPackagesPage } from './pages/packages.js';
import { renderEstimatorPage, initEstimator } from './pages/estimator.js';
import { renderProjectsPage, renderProjectDetailPage, initProjectsPage, initProjectDetailPage } from './pages/projects.js';
import { renderContactPage, initContactForm } from './pages/contact.js';

// Import Phase 2 pages
import { renderMaterialsPage } from './pages/materials.js';
import { renderLocationsPage, renderLocationDetailPage, initLocationsPage, initLocationDetailPage } from './pages/locations.js';
import { renderTestimonialsPage, initTestimonialsPage } from './pages/testimonials.js';
import { renderAboutPage, initAboutPage } from './pages/about.js';
import { renderBlogPage, renderBlogPostPage, initNewsletter } from './pages/blog.js';

// Import New Pages (Livspace-style)
import { renderHowItWorksPage, initHowItWorksPage } from './pages/how-it-works.js';
import { renderFAQPage, initFAQPage } from './pages/faq.js';
import { renderDesignIdeasPage, initDesignIdeasPage } from './pages/design-ideas.js';
import { renderReferPage, initReferPage } from './pages/refer.js';
import { renderLegalPage, initLegalPage } from './pages/legal.js';

// Import Auth pages
import { renderLoginPage, initLoginPage } from './pages/auth/login.js';
import { renderRegisterPage, initRegisterPage } from './pages/auth/register.js';
import { renderForgotPasswordPage, initForgotPasswordPage } from './pages/auth/forgot-password.js';

// Import Admin pages
import { renderAdminDashboard, initAdminDashboard } from './pages/admin/dashboard.js';
import { renderAdminLeads, initAdminLeads } from './pages/admin/leads.js';
import { renderAdminProjects, initAdminProjects } from './pages/admin/projects-admin.js';
import { renderAdminSettings, initAdminSettings } from './pages/admin/settings.js';
import { renderPricingAdmin, initPricingAdmin } from './pages/admin/pricing.js';
import { renderAboutAdmin, initAboutAdmin } from './pages/admin/about.js';
import { renderBlogAdmin, initBlogAdmin } from './pages/admin/blog.js';
import { renderAnalyticsAdmin, initAnalyticsAdmin } from './pages/admin/analytics.js';
import { renderCRMAdmin, initCRMAdmin } from './pages/admin/crm.js';
import { renderAdminInquiries, initAdminInquiries } from './pages/admin/inquiries.js';
import { renderAdminServices, initAdminServices } from './pages/admin/services.js';
import { renderAdminDesignIdeas, initAdminDesignIdeas } from './pages/admin/design-ideas.js';
import { renderAdminTestimonials, initAdminTestimonials } from './pages/admin/testimonials.js';

// Import Customer pages
import { renderCustomerDashboard, initCustomerDashboard } from './pages/customer/dashboard.js';
import { renderCustomerProjects, initCustomerProjects } from './pages/customer/projects.js';
import { renderCustomerPayments, initCustomerPayments } from './pages/customer/payments.js';
import { renderCustomerDocuments, initCustomerDocuments } from './pages/customer/documents.js';
import { renderProfilePage, initProfilePage } from './pages/customer/profile.js';

// App container
const appContainer = document.getElementById('app');

// Render layout wrapper (for public pages)
function renderLayout(pageContent) {
  return `
    ${renderHeader()}
    <main id="main-content">
      ${pageContent}
    </main>
    ${renderFooter()}
    ${renderStickyCTA()}
    ${renderBottomNav()}
    ${renderLeadModal()}
  `;
}

// Page render helper (with layout)
function renderPage(contentFn, initFn = null) {
  appContainer.innerHTML = renderLayout(contentFn());
  initUI();
  if (initFn) initFn();
  // Scroll to top on page change
  window.scrollTo(0, 0);
}

// Standalone page render (no header/footer - for auth and admin)
function renderStandalonePage(contentFn, initFn = null) {
  appContainer.innerHTML = contentFn();
  if (initFn) initFn();
  window.scrollTo(0, 0);
}

// Register Phase 1 routes
router
  .route('/', () => {
    renderPage(renderHomePage, initHomePage);
  })
  .route('/services', () => {
    renderPage(renderServicesPage, initServicesPage);
  })
  .route('/services/:slug', (params) => {
    renderPage(() => renderServiceDetailPage(params), () => initServiceDetailPage(params));
  })
  .route('/packages', () => {
    renderPage(renderPackagesPage);
  })
  .route('/estimator', () => {
    renderPage(renderEstimatorPage, initEstimator);
  })
  .route('/projects', () => {
    renderPage(renderProjectsPage, initProjectsPage);
  })
  .route('/projects/:slug', (params) => {
    renderPage(() => renderProjectDetailPage(params), () => initProjectDetailPage(params));
  })
  .route('/contact', () => {
    renderPage(renderContactPage, initContactForm);
  })
  // Phase 2 routes
  .route('/materials', () => {
    renderPage(renderMaterialsPage);
  })
  .route('/locations', () => {
    renderPage(renderLocationsPage, initLocationsPage);
  })
  .route('/locations/:slug', (params) => {
    renderPage(() => renderLocationDetailPage(params), () => initLocationDetailPage(params));
  })
  .route('/testimonials', () => {
    renderPage(renderTestimonialsPage, initTestimonialsPage);
  })
  .route('/about', () => {
    renderPage(renderAboutPage, initAboutPage);
  })
  .route('/blog', () => {
    renderPage(renderBlogPage, initNewsletter);
  })
  .route('/blog/:slug', (params) => {
    renderPage(() => renderBlogPostPage(params), initNewsletter);
  })
  // New Livspace-style routes
  .route('/how-it-works', () => {
    renderPage(renderHowItWorksPage, initHowItWorksPage);
  })
  .route('/faq', () => {
    renderPage(renderFAQPage, initFAQPage);
  })
  .route('/design-ideas', () => {
    renderPage(renderDesignIdeasPage, initDesignIdeasPage);
  })
  .route('/design-ideas/:category', (params) => {
    renderPage(() => renderDesignIdeasPage(params), initDesignIdeasPage);
  })
  .route('/refer', () => {
    renderPage(renderReferPage, initReferPage);
  })
  .route('/referrals', () => {
    renderPage(renderReferPage, initReferPage);
  })
  .route('/privacy', () => {
    renderPage(() => renderLegalPage('privacy'), initLegalPage);
  })
  .route('/terms', () => {
    renderPage(() => renderLegalPage('terms'), initLegalPage);
  })
  .route('/refund-policy', () => {
    renderPage(() => renderLegalPage('refund'), initLegalPage);
  })
  // Auth routes
  .route('/login', () => {
    renderStandalonePage(renderLoginPage, initLoginPage);
  })
  .route('/register', () => {
    renderStandalonePage(renderRegisterPage, initRegisterPage);
  })
  .route('/forgot-password', () => {
    renderStandalonePage(renderForgotPasswordPage, initForgotPasswordPage);
  })
  // Admin routes
  .route('/admin', () => {
    renderStandalonePage(renderAdminDashboard, initAdminDashboard);
  })
  .route('/admin/inquiries', () => {
    renderStandalonePage(renderAdminInquiries, initAdminInquiries);
  })
  .route('/admin/leads', () => {
    renderStandalonePage(renderAdminLeads, initAdminLeads);
  })
  .route('/admin/projects', () => {
    renderStandalonePage(renderAdminProjects, initAdminProjects);
  })
  .route('/admin/services', () => {
    renderStandalonePage(renderAdminServices, initAdminServices);
  })
  .route('/admin/design-ideas', () => {
    renderPage(renderAdminDesignIdeas, initAdminDesignIdeas);
  })
  .route('/admin/testimonials', () => {
    renderPage(renderAdminTestimonials, initAdminTestimonials);
  })
  .route('/admin/about', () => {
    renderStandalonePage(renderAboutAdmin, initAboutAdmin);
  })
  .route('/admin/users', () => {
    renderStandalonePage(renderAdminSettings, initAdminSettings);
  })
  .route('/admin/pricing', () => {
    renderStandalonePage(renderPricingAdmin, initPricingAdmin);
  })
  .route('/admin/content', () => {
    renderStandalonePage(renderBlogAdmin, initBlogAdmin);
  })
  .route('/admin/analytics', () => {
    renderStandalonePage(renderAnalyticsAdmin, initAnalyticsAdmin);
  })
  .route('/admin/crm', () => {
    renderStandalonePage(renderCRMAdmin, initCRMAdmin);
  })
  // Customer routes
  .route('/dashboard', () => {
    renderPage(renderCustomerDashboard, initCustomerDashboard);
  })
  .route('/my-projects', () => {
    renderPage(renderCustomerProjects, initCustomerProjects);
  })
  .route('/my-payments', () => {
    renderPage(renderCustomerPayments, initCustomerPayments);
  })
  .route('/my-documents', () => {
    renderPage(renderCustomerDocuments, initCustomerDocuments);
  })
  .route('/profile', () => {
    renderPage(renderProfilePage, initProfilePage);
  });

// Handle initial route
router.handleRoute();

console.log('NammaVeetuInteriors App Loaded — Full Platform Ready');
